import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Container,
  Drawer,
  IconButton,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { crmLevel } from "../access";
import { useDayCoach } from "./dayCoach/DayCoachContext";
import userService from "../api/userService";
import { getDefaultRoute } from "../access";
import {
  getAccessibleProducts,
  getVisibleNavItems,
  isNavItemActive,
  productFromPath,
  productHome,
  resolveActiveProduct,
  storeProduct,
} from "./navConfig";
import NotificationBell from "./NotificationBell";
import GroupChat from "./GroupChat";
import WeekGoal from "./WeekGoal";
import DayNextButton from "./dayCoach/DayNextButton";

const NAVBAR_HEIGHT = 72;

const palette = {
  bar: "var(--tz-bar)",
  border: "var(--tz-border)",
  text: "var(--tz-text-secondary)",
  textMuted: "var(--tz-text-muted)",
  textActive: "var(--tz-text)",
  hover: "var(--tz-hover)",
  active: "var(--tz-hover)",
  accent: "#6366f1",
  dev: "#ef4444",
  devMuted: "rgba(239, 68, 68, 0.1)",
  devBorder: "rgba(239, 68, 68, 0.2)",
};

function DevBadge({ label = "Dev" }) {
  return (
    <Tooltip title="Módulo en desarrollo" arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          ml: 0.75,
          px: 0.75,
          py: 0.25,
          borderRadius: "999px",
          bgcolor: palette.devMuted,
          border: `1px solid ${palette.devBorder}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: palette.dev,
            boxShadow: `0 0 0 2px rgba(239, 68, 68, 0.25)`,
            animation: "devPulse 2s ease-in-out infinite",
            "@keyframes devPulse": {
              "0%, 100%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.45, transform: "scale(0.88)" },
            },
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            color: "#dc2626",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function NavLink({ item, active, onClick, compact = false }) {
  const Icon = item.icon;
  const disabled = Boolean(item.disabled);

  const button = (
    <Box
      component="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      sx={{
        border: "none",
        background: active && !disabled ? palette.active : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 1.25 : 0,
        px: compact ? 2 : 1.75,
        py: compact ? 1.25 : 0.875,
        borderRadius: "999px",
        color: active ? palette.textActive : palette.text,
        fontFamily: "inherit",
        fontSize: compact ? "0.9375rem" : "0.8125rem",
        fontWeight: active ? 600 : 500,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": disabled
          ? { backgroundColor: "transparent" }
          : {
              backgroundColor: active ? palette.active : palette.hover,
              color: palette.textActive,
            },
        ...(active &&
          !compact && {
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 4,
              left: "50%",
              transform: "translateX(-50%)",
              width: 4,
              height: 4,
              borderRadius: "50%",
              backgroundColor: palette.accent,
            },
          }),
      }}
    >
      {compact && Icon && (
        <Icon sx={{ fontSize: 20, color: active ? palette.accent : palette.textMuted }} />
      )}
      {item.text}
      {item.inDevelopment && <DevBadge label={compact ? "En desarrollo" : "Dev"} />}
    </Box>
  );

  if (!disabled) return button;
  return (
    <Tooltip title="Este link solo es visible para desarrolladores" arrow placement="bottom">
      <span>{button}</span>
    </Tooltip>
  );
}

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [productsOpen, setProductsOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    effectiveUser,
    isPreviewing,
    logout,
    setAppearance,
    setPrivilegePreview,
    clearPrivilegePreview,
  } = useAuth();
  const accessUser = effectiveUser || user;
  const canViewAs = user?.account === "admin";
  const isMarketer = crmLevel(accessUser) === "marketer";
  const dayCoach = useDayCoach();

  const activeProduct = useMemo(
    () => resolveActiveProduct(accessUser, location.pathname),
    [accessUser, location.pathname]
  );
  const navItems = getVisibleNavItems(accessUser, activeProduct);
  const userMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    const fromPath = productFromPath(location.pathname);
    if (fromPath) storeProduct(fromPath);
  }, [location.pathname]);

  const initials = (() => {
    if (accessUser?.name) {
      const parts = accessUser.name.split(" ");
      return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
    }
    if (accessUser?.email) return accessUser.email.substring(0, 2).toUpperCase();
    return "US";
  })();

  const goTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const switchProduct = (productId) => {
    if (productId === activeProduct) return;
    storeProduct(productId);
    goTo(productHome(productId));
  };

  const products = getAccessibleProducts(accessUser);

  const enterAsUser = async (person) => {
    const id = person.id || person._id;
    if (!id) return;
    const mine = user?.id || user?._id;
    if (!isPreviewing && id === mine) {
      setAnchorEl(null);
      return;
    }
    try {
      const res = await userService.previewPrivileges(id);
      const nextUser = res.data?.user ?? res.data?.data;
      if (!nextUser) return;
      setPrivilegePreview(nextUser);
      setAnchorEl(null);
      navigate(getDefaultRoute(nextUser));
    } catch {
      /* la sesión real del admin sigue intacta */
    }
  };

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
    navigate("/login");
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: NAVBAR_HEIGHT,
          justifyContent: "center",
          backgroundColor: palette.bar,
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          color: palette.textActive,
          borderBottom: `1px solid ${palette.border}`,
          boxShadow: "0 1px 0 rgba(255,255,255,0.8) inset",
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 2, md: 3, xl: 4 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: `${NAVBAR_HEIGHT}px !important`,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              alignItems: "center",
              columnGap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, maxWidth: "100%", justifySelf: "start", overflow: "hidden" }}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  aria-label="Abrir menú"
                  sx={{
                    color: palette.text,
                    borderRadius: "12px",
                    "&:hover": { backgroundColor: palette.hover },
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography
                onClick={() => goTo(productHome(activeProduct))}
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  letterSpacing: "-0.04em",
                  color: "#0f172a",
                  fontFamily: "'Sora', system-ui, sans-serif",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                trazia
              </Typography>
              {activeProduct === "crm" && !isMarketer && <WeekGoal />}
              {activeProduct === "crm" && !isMarketer && <DayNextButton />}
            </Box>

            {!isMobile ? (
              <Box
                component="nav"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  justifySelf: "center",
                  position: "relative",
                  zIndex: 1,
                  px: 1,
                }}
              >
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    item={item}
                    active={isNavItemActive(location.pathname, item.path)}
                    onClick={() => goTo(item.path)}
                  />
                ))}
              </Box>
            ) : (
              <Box />
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, justifySelf: "end" }}>
            <GroupChat />
            <NotificationBell />
            <Box
              component="button"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                if (canViewAs && people.length === 0) {
                  userService.getAllUsers().then((res) => {
                    const list = res.data?.data || res.data?.users || [];
                    setPeople(Array.isArray(list) ? list : []);
                  }).catch(() => {});
                }
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                borderRadius: "10px",
                px: 1,
                py: 0.5,
                fontFamily: "inherit",
                transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: palette.hover },
              }}
            >
              {!isMobile && (
                <Typography
                  noWrap
                  sx={{
                    color: palette.textActive,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    maxWidth: 160,
                  }}
                >
                  {accessUser?.name || accessUser?.email || "Usuario"}
                </Typography>
              )}
              <Avatar
                src={accessUser?.avatar || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: !accessUser?.avatar ? palette.accent : "transparent",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {!accessUser?.avatar ? initials : ""}
              </Avatar>
            </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            backgroundColor: "var(--tz-surface)",
            borderRight: `1px solid ${palette.border}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 2,
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: palette.textMuted,
            }}
          >
            Navegación
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} size="small">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <List sx={{ px: 1.5, py: 1 }}>
          {navItems.map((item) => {
            const active = isNavItemActive(location.pathname, item.path);
            const Icon = item.icon;

            return (
              <ListItemButton
                key={item.path}
                onClick={() => {
                  if (item.disabled) return;
                  goTo(item.path);
                }}
                disabled={Boolean(item.disabled)}
                selected={active && !item.disabled}
                sx={{
                  borderRadius: "12px",
                  mb: 0.5,
                  py: 1.25,
                  "&.Mui-selected": {
                    backgroundColor: palette.active,
                    "&:hover": { backgroundColor: palette.active },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ fontSize: 20, color: active ? palette.accent : palette.textMuted }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                      {item.text}
                      {item.inDevelopment && <DevBadge label="En desarrollo" />}
                    </Box>
                  }
                  primaryTypographyProps={{
                    component: "div",
                    fontSize: "0.9375rem",
                    fontWeight: active ? 600 : 500,
                    color: active ? palette.textActive : palette.text,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        open={userMenuOpen}
        onClose={() => {
          setAnchorEl(null);
          setProductsOpen(false);
          setRolesOpen(false);
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: "14px",
            backgroundColor: "var(--tz-menu)",
            border: `1px solid ${palette.border}`,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${palette.border}` }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textActive }}>
            {user?.name || "Usuario"}
          </Typography>
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            {user?.email}
          </Typography>
          {isPreviewing && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: palette.accent, fontWeight: 600 }}>
              Previsualizando: {accessUser?.name || accessUser?.email}
            </Typography>
          )}
        </Box>
        {products.length > 1 && (
          <>
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                setProductsOpen((open) => !open);
              }}
              sx={{
                py: 1.25,
                fontSize: "0.875rem",
                color: palette.text,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: palette.textMuted }}>
                  Aplicacion
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: palette.textActive }}>
                  {products.find((product) => product.id === activeProduct)?.label}
                </Typography>
              </Box>
              <ExpandMoreRoundedIcon
                sx={{
                  fontSize: 18,
                  color: palette.textMuted,
                  transform: productsOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            </MenuItem>
            <Collapse in={productsOpen} timeout="auto" unmountOnExit>
              {products.map((product) => {
                const active = product.id === activeProduct;
                return (
                  <MenuItem
                    key={product.id}
                    onClick={() => {
                      setAnchorEl(null);
                      setProductsOpen(false);
                      switchProduct(product.id);
                    }}
                    sx={{
                      py: 1,
                      pl: 3,
                      fontSize: "0.875rem",
                      fontWeight: active ? 650 : 500,
                      color: active ? palette.textActive : palette.text,
                      backgroundColor: active ? palette.active : "transparent",
                    }}
                  >
                    {product.label}
                  </MenuItem>
                );
              })}
            </Collapse>
          </>
        )}
        {canViewAs && (
          <>
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                setRolesOpen((open) => !open);
              }}
              sx={{
                py: 1.25,
                fontSize: "0.875rem",
                color: palette.text,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: palette.textMuted }}>
                  Ver como
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: palette.textActive }}>
                  {isPreviewing ? accessUser?.name || accessUser?.email : "Mi cuenta"}
                </Typography>
              </Box>
              <ExpandMoreRoundedIcon
                sx={{
                  fontSize: 18,
                  color: palette.textMuted,
                  transform: rolesOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            </MenuItem>
            <Collapse in={rolesOpen} timeout="auto" unmountOnExit>
              {people.map((person) => {
                const id = person.id || person._id;
                const active = id === (accessUser?.id || accessUser?._id);
                return (
                  <MenuItem
                    key={id}
                    onClick={() => enterAsUser(person)}
                    sx={{
                      py: 1,
                      pl: 3,
                      fontSize: "0.875rem",
                      fontWeight: active ? 650 : 500,
                      color: active ? palette.textActive : palette.text,
                      backgroundColor: active ? palette.active : "transparent",
                    }}
                  >
                    {person.name || person.email}
                  </MenuItem>
                );
              })}
            </Collapse>
          </>
        )}
        {isPreviewing && (
          <MenuItem
            onClick={() => {
              clearPrivilegePreview();
              setAnchorEl(null);
              navigate(getDefaultRoute(user));
            }}
            sx={{ py: 1.25, fontSize: "0.875rem", fontWeight: 650, color: palette.textActive }}
          >
            Salir de la previsualización
          </MenuItem>
        )}
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            setAppearance(user?.appearance === "dark" ? "light" : "dark");
          }}
          sx={{
            py: 1.25,
            fontSize: "0.875rem",
            color: palette.text,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          Tema oscuro
          <Box
            aria-hidden
            sx={{
              width: 36,
              height: 20,
              borderRadius: "999px",
              flexShrink: 0,
              position: "relative",
              bgcolor: user?.appearance === "dark" ? "#f4f4f5" : "#e7e8ec",
              transition: "background-color 0.2s ease",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 2,
                left: 2,
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: user?.appearance === "dark" ? "#0f172a" : "#fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.18)",
                transform: user?.appearance === "dark" ? "translateX(16px)" : "translateX(0)",
                transition: "transform 0.2s ease",
              },
            }}
          />
        </MenuItem>
        {dayCoach?.enabled && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setProductsOpen(false);
              dayCoach.openBrief();
            }}
            sx={{
              py: 1.25,
              fontSize: "0.875rem",
              color: palette.text,
            }}
          >
            Resumen del día
          </MenuItem>
        )}
        {dayCoach?.canReset && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setProductsOpen(false);
              dayCoach.resetDayBrief().catch(() => {});
            }}
            sx={{
              py: 1.25,
              fontSize: "0.875rem",
              color: palette.text,
            }}
          >
            Reiniciar aviso del día
          </MenuItem>
        )}
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.25,
            color: "#dc2626",
            fontSize: "0.875rem",
            "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.06)" },
          }}
        >
          Cerrar sesión
        </MenuItem>
      </Menu>
    </>
  );
}

export { NAVBAR_HEIGHT };
